<template>
  <div v-if="localSettings">
    <base-form-group>
      <base-checkbox name="checkbox-watch-hr" v-model="localSettings.opt_watch_hr">{{
        $t('src/components/coursesettingsform.8890')
      }}</base-checkbox>
      <keep-alive>
        <div v-if="isWatchHR">
          <div>
            <base-range-two-inputs
              :label="$t('general.limits')"
              :start-value="localSettings.course_hr_min"
              :end-value="localSettings.course_hr_max"
              :min="minRate"
              :max="maxRate"
              :step="1"
              class="form-group-medium"
              @input="changeRate"
              @set:interval="setInterval"
              ref="hr"
            ></base-range-two-inputs>
          </div>
          <patient-max-hr :patient="patient"></patient-max-hr>
        </div>
      </keep-alive>
    </base-form-group>
    <base-form-group>
      <base-checkbox name="checkbox-arrythmias" v-model="localSettings.opt_calc_arrythmias">{{
        $t('src/components/coursesettingsform.6478')
      }}</base-checkbox>
    </base-form-group>
    <base-form-group>
      <base-checkbox name="checkbox-calc-st" v-model="localSettings.opt_calc_st">{{
        $t('src/components/coursesettingsform.CalculateST')
      }}</base-checkbox>
    </base-form-group>
    <base-form-group>
      <base-checkbox name="checkbox-borg" v-model="localSettings.opt_borg">{{
        $t('sessionsInfo.borgScale')
      }}</base-checkbox>
    </base-form-group>
    <base-form-group>
      <base-checkbox name="checkbox-show-events" v-model="localSettings.opt_show_events">{{
        $t('src/components/coursesettingsform.9538')
      }}</base-checkbox>

      <div v-if="isWatchEvents">
        <div>
          <div class="d-flex align-items-center justify-content-between mt-2">
            <div class="text-muted text-size-95 text-nowrap">
              <span class="dot dot-danger">●</span>
              <span class="">{{ $t('src/components/coursesettingsform.particularyImportantEvents') }} </span>
            </div>

            <base-dropdown-select
              :options="listOfReactions"
              v-model.number="localSettings.opt_reaction_red"
              class="form-control ml-2"
              style="min-width: 227px"
            ></base-dropdown-select>
          </div>
        </div>

        <div>
          <div class="d-flex align-items-center justify-content-between mt-2">
            <div class="text-muted text-size-95 text-nowrap">
              <span class="dot dot-warning">●</span>
              <span class="">{{ $t('src/components/coursesettingsform.importantEvents') }} </span>
            </div>

            <base-dropdown-select
              :options="listOfReactions"
              :position="'up'"
              v-model.number="localSettings.opt_reaction_yellow"
              class="form-control ml-2"
              style="min-width: 227px"
            ></base-dropdown-select>
          </div>
        </div>
      </div>
    </base-form-group>

    <base-form-group>
      <base-checkbox name="checkbox-show-duration" v-model="localSettings.opt_show_duration">{{
        $t('src/components/coursesettingsform.4230')
      }}</base-checkbox>
      <base-form-group
        v-if="isWatchDuration"
        :is-invalid="$v.localSettings.training_duration.$error"
        :errors="{
          required: !$v.localSettings.training_duration.required,
          numeric: !$v.localSettings.training_duration.numeric,
          minValue: !$v.localSettings.training_duration.minValue,
          maxValue: !$v.localSettings.training_duration.maxValue
        }"
        :messages="{
          minValue: $t('errorMessage.minDurationSession'),
          maxValue: $t('errorMessage.maxDurationSession')
        }"
      >
        <label class="form-label text-nowrap">{{ $t('src/components/coursesettingsform.8850') }}</label>
        <duration-with-unit
          :is-invalid="$v.localSettings.training_duration.$error"
          v-model="localSettings.training_duration"
          :durationUnit="localSettings.training_duration_unit"
          @change:durationUnit="changeDurationUnit"
        ></duration-with-unit>
      </base-form-group>
    </base-form-group>
    <base-form-group v-if="isWatchDuration">
      <base-checkbox name="auto_session_stop" v-model="localSettings.auto_session_stop">{{
        $t('src/components/coursesettingsform.5116')
      }}</base-checkbox>
    </base-form-group>
  </div>
</template>

<script>
import { HEART_RATE } from '@/main.config.js'
import { requiredIf, numeric, minValue, maxValue, decimal } from 'vuelidate/lib/validators'
import { patientMaxHR } from '@/mixins/patient-max-hr'

import('@/styles/components/filter.less')

export default {
  name: 'CourseSettingsForm',
  mixins: [patientMaxHR],
  components: {
    BaseFormGroup: () => import('@/base/BaseFormGroup.vue'),
    BaseCheckbox: () => import('@/base/BaseCheckbox.vue'),
    BaseRangeTwoInputs: () => import('@/base/BaseRangeTwoInputs.vue'),
    BaseDropdownSelect: () => import('@/base/BaseDropdownSelect.vue'),
    DurationWithUnit: () => import('@/components/DurationWithUnit.vue'),
    PatientMaxHr: () => import('@/components/PatientMaxHr.vue')
  },
  props: {
    settings: {
      type: Object,
      required: true
    },
    patient: {
      type: Object,
      required: true
    }
  },
  data: function() {
    return {
      minRate: HEART_RATE.min,
      maxRate: HEART_RATE.max,
      localSettings: {
        training_duration: null,
        training_duration_unit: null,
        course_hr_min: null,
        course_hr_max: null,
        opt_reaction_red: 1,
        opt_reaction_yellow: 1
      },
      listOfReactions: {
        1: this.$t('src/components/coursesettingsform.noAction'),
        2: this.$t('src/components/coursesettingsform.notifyPatient'),
        3: this.$t('src/components/coursesettingsform.stopTraining')
      }
    }
  },
  validations() {
    return {
      localSettings: {
        training_duration: {
          required: requiredIf(function() {
            return this.isWatchDuration
          }),
          numeric: this.getType(),
          minValue: minValue(this.minValue),
          maxValue: maxValue(this.maxValue)
        },
        course_hr_min: {
          required: requiredIf(function() {
            return this.isWatchHR
          }),
          numeric: numeric
        },
        course_hr_max: {
          required: requiredIf(function() {
            return this.isWatchHR
          }),
          numeric: numeric
        }
      }
    }
  },
  computed: {
    isWatchHR() {
      return this.localSettings.opt_watch_hr
    },
    isWatchDuration() {
      return this.localSettings.opt_show_duration
    },
    isWatchEvents() {
      return false
      // Вернуть, когда в мобильном приложение добавиться поддержка реакций системы
      //return this.localSettings.opt_show_events
    },
    minValue() {
      if (this.localSettings.training_duration_unit == 1) return 5
      else return 0.1
    },
    maxValue() {
      if (this.localSettings.training_duration_unit == 1) return 1440
      else return 24
    }
  },
  watch: {
    localSettings() {
      this.$v.$touch()
      this.$emit('change:settings', this.localSettings)
    },
    '$v.$invalid': {
      handler() {
        this.$emit('set:invalid', this.$v.$invalid)
      }
    }
  },

  created() {
    this.localSettings = Object.assign({}, this.localSettings, this.settings)
    //если меняем опцию слежения за чсс, то установим значения по умолчанию и отменим наблюдение
    let unwatchOptHR = this.$watch('localSettings.opt_watch_hr', newVal => {
      if (
        newVal &&
        (this.localSettings.course_hr_min === null || this.localSettings.course_hr_max === null)
      ) {
        this.localSettings.course_hr_min = HEART_RATE.defaultStart
        this.localSettings.course_hr_max = HEART_RATE.defaultEnd
        unwatchOptHR()
      }
    })
  },
  methods: {
    changeRate(min, max) {
      this.localSettings.course_hr_min = min
      this.localSettings.course_hr_max = max
    },
    changeDurationUnit(value) {
      this.localSettings.training_duration_unit = value
    },
    getType() {
      if (this.localSettings.training_duration_unit === 1) {
        return numeric
      } else return decimal
    },
    setInterval() {
      let targetHr = this.maxHr * 0.75
      let diffHr = targetHr * 0.1
      diffHr = diffHr < 10 ? 10 : Math.round(diffHr)
      let startInt = Math.round(targetHr - diffHr)
      let endInt = Math.round(targetHr + diffHr)
      this.localSettings.course_hr_min = startInt
      this.localSettings.course_hr_max = endInt
      this.$refs.hr.changeStartValue(startInt)
      this.$refs.hr.changeEndValue(endInt)
    }
  }
}
</script>
